import { jsPDF } from 'jspdf';
import { TicketData, espacioLogoPx, espacioLogoArribaPx, anchoRolloMm } from './ticket';

/* Exporta el ticket a PDF con el mismo formato de rollo que sale por la
   impresora térmica (58 u 80 mm de ancho, alto variable según los productos).
   Se dibuja con texto, no como captura de pantalla: el PDF pesa unos KB y el
   texto se puede buscar y copiar. */

const METODO_LABEL: Record<string, string> = {
  efectivo:      'Efectivo',
  tarjeta:       'Tarjeta',
  transferencia: 'Transferencia',
  qr:            'Codigo QR',
};

const money = (n: number) => `$${(Number(n) || 0).toFixed(2)}`;

/* Dibuja el ticket en un documento del alto indicado y devuelve cuánto ocupó
   de verdad. Se llama dos veces: una para medir y otra ya con el alto exacto,
   porque jsPDF no deja redimensionar la página después de dibujarla. */
function construir(ticket: TicketData, altoMm: number): { doc: jsPDF; alto: number } {
  const anchoMm = anchoRolloMm(ticket);
  const margen  = 4;
  const util    = anchoMm - margen * 2;

  const doc = new jsPDF({ unit: 'mm', format: [anchoMm, altoMm] });
  doc.setFont('courier', 'normal');

  let y = margen;
  const salto = (mm = 4) => { y += mm; };

  const texto = (txt: string, opts: { size?: number; bold?: boolean; align?: 'left' | 'center' | 'right' } = {}) => {
    doc.setFontSize(opts.size ?? 8);
    doc.setFont('courier', opts.bold ? 'bold' : 'normal');
    const x = opts.align === 'center' ? anchoMm / 2
            : opts.align === 'right'  ? anchoMm - margen
            : margen;
    doc.text(txt, x, y, { align: opts.align ?? 'left' });
  };

  // Línea de dos columnas: concepto a la izquierda, importe a la derecha
  const fila = (izq: string, der: string, opts: { size?: number; bold?: boolean } = {}) => {
    texto(izq, opts);
    if (der) texto(der, { ...opts, align: 'right' });
    salto((opts.size ?? 8) > 9 ? 5.5 : 4);
  };

  const separador = () => {
    doc.setLineWidth(0.1);
    doc.setLineDashPattern([0.6, 0.6], 0);
    doc.line(margen, y, anchoMm - margen, y);
    doc.setLineDashPattern([], 0);
    salto(3);
  };

  // Texto largo partido al ancho del rollo
  const parrafo = (txt: string, opts: { size?: number; bold?: boolean; align?: 'left' | 'center' } = {}) => {
    doc.setFontSize(opts.size ?? 8);
    doc.setFont('courier', opts.bold ? 'bold' : 'normal');
    for (const linea of doc.splitTextToSize(txt, util)) {
      texto(linea, opts);
      salto((opts.size ?? 8) > 10 ? 5 : 3.6);
    }
  };

  // ── Encabezado ───────────────────────────────────────────────────────────
  if (ticket.logo_base64) {
    try {
      const pct = Math.min(100, Math.max(20, parseInt(String(ticket.logo_size)) || 70));
      const ancho = (util * pct) / 100;
      const props = doc.getImageProperties(ticket.logo_base64);
      const alto = (props.height / props.width) * ancho;   // sin deformar el logo
      salto(espacioLogoArribaPx(ticket) / 2);
      doc.addImage(ticket.logo_base64, (anchoMm - ancho) / 2, y, ancho, alto);
      // El espacio configurado viene en px de pantalla; en el rollo son mm
      // (los 6 px por omisión son los 3 mm que llevaba fijos antes).
      salto(alto + espacioLogoPx(ticket) / 2);
    } catch { /* logo ilegible: el ticket sale sin él */ }
  }

  parrafo(ticket.restaurante || 'CHARGOY', { size: 12, bold: true, align: 'center' });
  if (ticket.slogan)    parrafo(ticket.slogan,             { size: 7, align: 'center' });
  if (ticket.direccion) parrafo(ticket.direccion,          { size: 7, align: 'center' });
  if (ticket.telefono)  parrafo(`Tel: ${ticket.telefono}`, { size: 7, align: 'center' });
  if (ticket.rfc)       parrafo(`RFC: ${ticket.rfc}`,      { size: 7, align: 'center' });
  salto(1);
  separador();

  // ── Datos de la venta ────────────────────────────────────────────────────
  const fecha = new Date(ticket.fecha).toLocaleString('es-MX', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const sinMesa = ticket.mesa_numero === null || ticket.mesa_numero === undefined || ticket.mesa_numero === '';
  const origen = sinMesa ? `Para llevar: ${ticket.cliente_nombre || 'Mostrador'}` : `Mesa: ${ticket.mesa_numero}`;
  parrafo(`${origen}   Mesero: ${ticket.mesero}`, { size: 7.5 });
  parrafo(`Fecha: ${fecha}`, { size: 7.5 });
  separador();

  // ── Productos ────────────────────────────────────────────────────────────
  const sinPrecios = !!ticket.precuenta && ticket.precuenta_mostrar_precios === 'false';
  fila('PRODUCTO', sinPrecios ? '' : 'IMPORTE', { size: 7, bold: true });

  for (const item of ticket.items) {
    const yLinea = y;                       // el importe va a la altura del nombre
    const desc = Number(item.descuento_monto || 0);
    // El importe ya trae los extras sumados: en el renglón va solo el producto y
    // cada extra con su monto abajo, para que no parezca que se cobra dos veces.
    const mods = item.modificadores || [];
    const extras = mods.reduce((s, m) => s + Number(m.precio_extra || 0) * item.cantidad, 0);
    parrafo(`${item.cantidad}x ${item.producto}`, { size: 8 });
    if (!sinPrecios) {
      doc.setFontSize(8);
      doc.setFont('courier', 'normal');
      doc.text(money(item.importe - extras), anchoMm - margen, yLinea, { align: 'right' });
    }
    const n = item.cantidad > 1 ? `${item.cantidad}x ` : '';
    for (const m of mods) {
      const extra = Number(m.precio_extra || 0) * item.cantidad;
      if (extra > 0 && !sinPrecios) fila(`   · ${n}${m.nombre}`, `+${money(extra)}`, { size: 7 });
      else parrafo(`   · ${n}${m.nombre}`, { size: 7 });
    }
    // El descuento se muestra donde se aplicó, con lo que queda a pagar
    if (desc > 0 && !sinPrecios) {
      const label = desc >= item.importe - 0.01
        ? 'Cortesía'
        : `Descuento ${Math.round(desc / item.importe * 100)}%`;
      fila(`   ${label}:`, `-${money(desc)}`, { size: 7 });
      fila('   Queda en:', money(item.importe - desc), { size: 7 });
    }
    for (const sub of item.promo_items || []) {
      parrafo(`   · ${sub.cantidad}x ${sub.producto}`, { size: 7 });
    }
    // Las notas son indicaciones para cocina/barra: no se imprimen en el ticket del cliente
  }
  salto(1);
  separador();

  // ── Totales ──────────────────────────────────────────────────────────────
  if (!sinPrecios) {
    fila('Subtotal:', money(ticket.subtotal));
    if (ticket.ticket_mostrar_iva !== 'false') fila('IVA:', money(ticket.iva));
    if ((ticket.descuento_monto ?? 0) > 0) {
      const etiqueta = ticket.descuento_tipo === 'porcentaje'
        ? `Descuento (${ticket.descuento_valor}%):`
        : ticket.descuento_tipo === 'item' ? 'Descuento en productos:' : 'Descuento:';
      fila(etiqueta, `-${money(ticket.descuento_monto ?? 0)}`);
    }
    fila('TOTAL:', money(ticket.total), { size: 11, bold: true });
    separador();
  }

  // ── Pago ─────────────────────────────────────────────────────────────────
  if (ticket.precuenta) {
    parrafo(ticket.precuenta_titulo || 'PRE-CUENTA', { size: 9, bold: true, align: 'center' });
    parrafo(ticket.precuenta_mensaje || 'ESTE NO ES UN COMPROBANTE FISCAL', { size: 7, align: 'center' });
  } else {
    if (ticket.metodo_2 && ticket.monto_metodo_1 != null && ticket.monto_metodo_2 != null) {
      fila(`${METODO_LABEL[ticket.metodo] || ticket.metodo}:`, money(ticket.monto_metodo_1));
      fila(`${METODO_LABEL[ticket.metodo_2] || ticket.metodo_2}:`, money(ticket.monto_metodo_2));
    } else {
      fila('Metodo:', METODO_LABEL[ticket.metodo] || ticket.metodo);
      fila('Recibido:', money(ticket.monto_recibido));
    }
    if (ticket.propina > 0 && ticket.ticket_mostrar_propina !== 'false') {
      fila(`Propina${ticket.propina_pct ? ` (${ticket.propina_pct}%)` : ''}:`, money(ticket.propina));
    }
    if (ticket.cambio > 0 && ticket.ticket_mostrar_cambio !== 'false') {
      fila('Cambio:', money(ticket.cambio), { bold: true });
    }
  }
  separador();

  // ── Pie ──────────────────────────────────────────────────────────────────
  let lineasExtra: { nombre?: string; texto?: string }[] = [];
  try { lineasExtra = JSON.parse(ticket.ticket_lineas_extra || '[]'); } catch { lineasExtra = []; }
  for (const l of lineasExtra.filter(l => l.texto)) {
    parrafo(`${l.nombre ? `${l.nombre}: ` : ''}${l.texto}`, { size: 7, align: 'center' });
  }
  parrafo(ticket.mensaje_pie || '¡Gracias por su visita!', { size: 7.5, align: 'center' });

  return { doc, alto: y + margen };
}

export function generarTicketPdf(ticket: TicketData): jsPDF {
  // 1ª pasada sobre un rollo largo solo para saber cuánto mide el ticket;
  // 2ª pasada ya con el alto justo, para que el PDF no salga con media hoja
  // en blanco al abrirlo ni desperdicie papel al imprimirlo.
  const medida = construir(ticket, 1000);
  return construir(ticket, medida.alto).doc;
}

/** Descarga el ticket como archivo PDF. */
export function descargarTicketPdf(ticket: TicketData, nombreArchivo?: string): void {
  const doc = generarTicketPdf(ticket);
  const f = new Date(ticket.fecha);
  const dosDigitos = (n: number) => String(n).padStart(2, '0');
  const sello = `${f.getFullYear()}${dosDigitos(f.getMonth() + 1)}${dosDigitos(f.getDate())}-${dosDigitos(f.getHours())}${dosDigitos(f.getMinutes())}`;
  const quien = ticket.mesa_numero ? `mesa${ticket.mesa_numero}` : 'ticket';
  doc.save(nombreArchivo || `ticket-${quien}-${sello}.pdf`);
}
